import { useState, useEffect, useCallback } from "react";
import {
  adminPlansService,
  AdminPlan,
  PlanAssignment,
  EnterprisePlanRequest,
  EnterpriseRequestStatus,
} from "../../services/admin/plans.admin.service";
import {
  adminBrandsService,
  AdminBrand,
} from "../../services/admin/brands.admin.service";
import { DataTable } from "@/components/ui/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Plus,
  Edit,
  RefreshCw,
  CreditCard,
  CheckCircle,
  EyeOff,
  Building2,
  Lock,
  Globe,
  Trash2,
  Search,
  Mail,
  Sparkles,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useDebounce } from "../../hooks/useDebounce";

const NO_COPY_VALUE = "none";

const REQUEST_STATUS_STYLES: Record<EnterpriseRequestStatus, string> = {
  NEW: "bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400",
  CONTACTED: "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400",
  CLOSED: "bg-muted text-muted-foreground",
};

const REQUEST_STATUSES: EnterpriseRequestStatus[] = [
  "NEW",
  "CONTACTED",
  "CLOSED",
];

/** Unworked requests first, then newest — the backend returns them newest-first */
function sortRequests(requests: EnterprisePlanRequest[]) {
  return [...requests].sort(
    (a, b) =>
      REQUEST_STATUSES.indexOf(a.status) - REQUEST_STATUSES.indexOf(b.status),
  );
}

type PlanFormState = {
  name: string;
  price: string;
  commissionPct: string;
  aiSearchQuestions: string;
  isPublic: boolean;
  copyFromPlanId: string;
  isActive: boolean;
};

const emptyForm: PlanFormState = {
  name: "",
  price: "",
  commissionPct: "0",
  aiSearchQuestions: "",
  isPublic: false,
  copyFromPlanId: NO_COPY_VALUE,
  isActive: true,
};

function parseForm(form: PlanFormState) {
  const price = Number(form.price);
  if (form.name.trim() === "") {
    toast.error("Plan name is required");
    return null;
  }
  if (Number.isNaN(price) || price < 0) {
    toast.error("Price must be a non-negative number");
    return null;
  }
  const commissionPct = Number(form.commissionPct || 0);
  if (Number.isNaN(commissionPct) || commissionPct < 0 || commissionPct >= 100) {
    toast.error("Revenue share must be a percentage below 100");
    return null;
  }
  const aiSearchQuestions =
    form.aiSearchQuestions.trim() === "" ? null : Number(form.aiSearchQuestions);
  if (aiSearchQuestions !== null && (Number.isNaN(aiSearchQuestions) || aiSearchQuestions < 0)) {
    toast.error("Social searches must be a non-negative number");
    return null;
  }
  return {
    name: form.name.trim(),
    price,
    commissionRate: commissionPct / 100,
    aiSearchQuestions,
    isPublic: form.isPublic,
  };
}

export default function AdminPlans() {
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<AdminPlan | null>(null);
  const [form, setForm] = useState<PlanFormState>(emptyForm);

  // Assignments modal state
  const [assignments, setAssignments] = useState<PlanAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  const debouncedBrandSearch = useDebounce(brandSearch.trim(), 300);
  const [brandResults, setBrandResults] = useState<AdminBrand[]>([]);
  const [searchingBrands, setSearchingBrands] = useState(false);
  const [assigningSessionId, setAssigningSessionId] = useState<string | null>(
    null,
  );
  const [removingAssignmentId, setRemovingAssignmentId] = useState<
    string | null
  >(null);

  // Enterprise ("Custom" tier) requests submitted by brands from the pricing page
  const [enterpriseRequests, setEnterpriseRequests] = useState<
    EnterprisePlanRequest[]
  >([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [updatingRequestId, setUpdatingRequestId] = useState<string | null>(
    null,
  );
  const [selectedRequest, setSelectedRequest] =
    useState<EnterprisePlanRequest | null>(null);
  // Set while creating a plan straight out of a request, so the new plan is
  // assigned to that store as soon as it exists
  const [requestForNewPlan, setRequestForNewPlan] =
    useState<EnterprisePlanRequest | null>(null);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminPlansService.getAllPlans();
      setPlans(data);
    } catch (error: any) {
      console.error("Failed to fetch plans:", error);
      toast.error(error.response?.data?.message || "Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEnterpriseRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const data = await adminPlansService.getEnterpriseRequests();
      setEnterpriseRequests(sortRequests(data));
    } catch (error: any) {
      console.error("Failed to fetch enterprise requests:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch enterprise requests",
      );
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchEnterpriseRequests();
  }, [fetchPlans, fetchEnterpriseRequests]);

  useEffect(() => {
    if (!isAssignModalOpen || debouncedBrandSearch.length < 2) {
      setBrandResults([]);
      return;
    }
    let cancelled = false;
    setSearchingBrands(true);
    adminBrandsService
      .searchBrands({
        keyword: debouncedBrandSearch,
        searchBy: "COMPANY",
        limit: 8,
        page: 1,
      })
      .then((res) => {
        if (!cancelled) setBrandResults(res.data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to search brands");
      })
      .finally(() => {
        if (!cancelled) setSearchingBrands(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedBrandSearch, isAssignModalOpen]);

  const handleCreate = () => {
    const scale = plans.find((p) => p.name === "Scale");
    setRequestForNewPlan(null);
    setForm({
      ...emptyForm,
      copyFromPlanId: scale?.id ?? NO_COPY_VALUE,
    });
    setIsCreateModalOpen(true);
  };

  /** Create-plan flow started from an Enterprise request: private plan, auto-assigned */
  const handleCreatePlanForRequest = (request: EnterprisePlanRequest) => {
    const scale = plans.find((p) => p.name === "Scale");
    const suggestedName = request.companyName?.trim() || request.shop;
    setRequestForNewPlan(request);
    setSelectedRequest(null);
    setForm({
      ...emptyForm,
      name: suggestedName,
      isPublic: false,
      copyFromPlanId: scale?.id ?? NO_COPY_VALUE,
    });
    setIsCreateModalOpen(true);
  };

  const handleEdit = (plan: AdminPlan) => {
    setSelectedPlan(plan);
    setForm({
      name: plan.name,
      price: String(plan.price),
      commissionPct: String(plan.commissionRate * 100),
      aiSearchQuestions:
        plan.aiSearchQuestions === null ? "" : String(plan.aiSearchQuestions),
      isPublic: plan.isPublic,
      copyFromPlanId: NO_COPY_VALUE,
      isActive: plan.isActive,
    });
    setIsEditModalOpen(true);
  };

  const handleCreateSubmit = async () => {
    const parsed = parseForm(form);
    if (!parsed) return;

    setIsSubmitting(true);
    try {
      const created = await adminPlansService.createPlan({
        ...parsed,
        copyFromPlanId:
          form.copyFromPlanId === NO_COPY_VALUE
            ? undefined
            : form.copyFromPlanId,
      });
      toast.success(`Plan "${parsed.name}" created`);

      // Started from an Enterprise request: give that store access right away
      if (requestForNewPlan && created?.plan?.id) {
        await adminPlansService.assignBrand(created.plan.id, {
          sessionId: requestForNewPlan.sessionId,
        });
        if (requestForNewPlan.status === "NEW") {
          await adminPlansService.updateEnterpriseRequestStatus(
            requestForNewPlan.id,
            "CONTACTED",
          );
        }
        toast.success(
          `${requestForNewPlan.companyName || requestForNewPlan.shop} can now see "${parsed.name}"`,
        );
        setRequestForNewPlan(null);
        fetchEnterpriseRequests();
      }

      setIsCreateModalOpen(false);
      fetchPlans();
    } catch (error: any) {
      console.error("Failed to create plan:", error);
      toast.error(error.response?.data?.message || "Failed to create plan");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedPlan) return;
    const parsed = parseForm(form);
    if (!parsed) return;

    setIsSubmitting(true);
    try {
      await adminPlansService.updatePlan(selectedPlan.id, {
        ...parsed,
        isActive: form.isActive,
      });
      toast.success(`Plan "${parsed.name}" updated`);
      setIsEditModalOpen(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (error: any) {
      console.error("Failed to update plan:", error);
      toast.error(error.response?.data?.message || "Failed to update plan");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (plan: AdminPlan) => {
    try {
      await adminPlansService.updatePlan(plan.id, { isActive: !plan.isActive });
      toast.success(
        `Plan "${plan.name}" ${plan.isActive ? "deactivated" : "activated"}`,
      );
      fetchPlans();
    } catch (error: any) {
      console.error("Failed to update plan status:", error);
      toast.error(
        error.response?.data?.message || "Failed to update plan status",
      );
    }
  };

  const refreshAssignments = async (planId: string) => {
    setLoadingAssignments(true);
    try {
      const data = await adminPlansService.getAssignments(planId);
      setAssignments(data.assignments);
    } catch (error: any) {
      console.error("Failed to fetch assignments:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch assigned brands",
      );
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleManageBrands = (plan: AdminPlan) => {
    setSelectedPlan(plan);
    setAssignments(plan.assignments);
    setBrandSearch("");
    setBrandResults([]);
    setIsAssignModalOpen(true);
    refreshAssignments(plan.id);
  };

  const handleAssignBrand = async (brand: AdminBrand) => {
    if (!selectedPlan) return;
    setAssigningSessionId(brand.sessionId);
    try {
      await adminPlansService.assignBrand(selectedPlan.id, {
        sessionId: brand.sessionId,
      });
      toast.success(
        `${brand.companyName || brand.email} can now use "${selectedPlan.name}"`,
      );
      await refreshAssignments(selectedPlan.id);
      fetchPlans();
    } catch (error: any) {
      console.error("Failed to assign brand:", error);
      toast.error(error.response?.data?.message || "Failed to assign brand");
    } finally {
      setAssigningSessionId(null);
    }
  };

  const handleRemoveAssignment = async (assignment: PlanAssignment) => {
    if (!selectedPlan) return;
    setRemovingAssignmentId(assignment.id);
    try {
      await adminPlansService.removeAssignment(selectedPlan.id, assignment.id);
      toast.success(
        `Removed ${assignment.companyName || assignment.shop} from "${selectedPlan.name}"`,
      );
      await refreshAssignments(selectedPlan.id);
      fetchPlans();
    } catch (error: any) {
      console.error("Failed to remove assignment:", error);
      toast.error(
        error.response?.data?.message || "Failed to remove assignment",
      );
    } finally {
      setRemovingAssignmentId(null);
    }
  };

  const handleUpdateRequestStatus = async (
    request: EnterprisePlanRequest,
    status: EnterpriseRequestStatus,
  ) => {
    if (status === request.status) return;
    setUpdatingRequestId(request.id);
    try {
      await adminPlansService.updateEnterpriseRequestStatus(request.id, status);
      setEnterpriseRequests((current) =>
        current.map((r) => (r.id === request.id ? { ...r, status } : r)),
      );
      setSelectedRequest((current) =>
        current?.id === request.id ? { ...current, status } : current,
      );
      toast.success(`Request marked ${status.toLowerCase()}`);
    } catch (error: any) {
      console.error("Failed to update request status:", error);
      toast.error(
        error.response?.data?.message || "Failed to update request status",
      );
    } finally {
      setUpdatingRequestId(null);
    }
  };

  const assignedSessionIds = new Set(assignments.map((a) => a.sessionId));
  const openRequestCount = enterpriseRequests.filter(
    (r) => r.status !== "CLOSED",
  ).length;

  const requestColumns: ColumnDef<EnterprisePlanRequest>[] = [
    {
      id: "brand",
      header: "Brand",
      meta: { width: "w-40" },
      cell: ({ row }) => (
        <p className="font-medium text-foreground truncate">
          {row.original.companyName || row.original.shop}
        </p>
      ),
    },
    {
      id: "shop",
      header: "Shop",
      meta: { width: "w-44" },
      cell: ({ row }) => (
        <p className="text-sm text-muted-foreground truncate">
          {row.original.shop}
        </p>
      ),
    },
    {
      id: "contact",
      header: "Contact",
      meta: { width: "w-36" },
      cell: ({ row }) => (
        <p className="text-foreground truncate">{row.original.contactName}</p>
      ),
    },
    {
      id: "email",
      header: "Email",
      meta: { width: "w-52" },
      cell: ({ row }) => (
        <a
          href={`mailto:${row.original.email}`}
          className="text-sm text-[#d7785b] hover:underline truncate inline-flex items-center gap-1 max-w-full"
        >
          <Mail size={12} className="shrink-0" />
          <span className="truncate">{row.original.email}</span>
        </a>
      ),
    },
    {
      id: "phone",
      header: "Phone",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <p className="text-sm text-muted-foreground truncate">
          {row.original.phone || "—"}
        </p>
      ),
    },
    {
      id: "monthlyRevenue",
      header: "Monthly Revenue",
      meta: { width: "w-36" },
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {row.original.monthlyRevenue || "—"}
        </div>
      ),
    },
    {
      id: "currentPlan",
      header: "Current Plan",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {row.original.currentPlan || "None"}
        </div>
      ),
    },
    {
      id: "createdAt",
      header: "Requested",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {format(new Date(row.original.createdAt), "MMM d, yyyy")}
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      meta: { width: "w-40" },
      cell: ({ row: { original: request } }) => (
        <Select
          value={request.status}
          onValueChange={(value) =>
            handleUpdateRequestStatus(request, value as EnterpriseRequestStatus)
          }
          disabled={updatingRequestId === request.id}
        >
          <SelectTrigger
            className={`h-8 w-[130px] border-0 text-xs font-medium ${REQUEST_STATUS_STYLES[request.status]}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REQUEST_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {status.charAt(0) + status.slice(1).toLowerCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      meta: { width: "w-20" },
      cell: ({ row: { original: request } }) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleCreatePlanForRequest(request)}
            className="p-1.5 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
            title="Create a private plan for this brand"
          >
            <Sparkles size={16} />
          </button>
        </div>
      ),
    },
  ];

  const columns: ColumnDef<AdminPlan>[] = [
    {
      id: "name",
      header: "Plan",
      meta: { width: "w-48" },
      cell: ({ row: { original: plan } }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{plan.name}</span>
          {!plan.isPublic && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300">
              <Lock size={12} />
              Private
            </span>
          )}
        </div>
      ),
    },
    {
      id: "price",
      header: "Price",
      meta: { width: "w-28" },
      cell: ({ row: { original: plan } }) => (
        <div className="text-foreground font-medium">
          ${plan.price}
          <span className="text-muted-foreground font-normal">/mo</span>
        </div>
      ),
    },
    {
      id: "commissionRate",
      header: "Rev Share",
      meta: { width: "w-28" },
      cell: ({ row: { original: plan } }) => (
        <div className="text-muted-foreground">
          {(plan.commissionRate * 100).toFixed(
            (plan.commissionRate * 100) % 1 === 0 ? 0 : 2,
          )}
          %
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      meta: { width: "w-28" },
      cell: ({ row: { original: plan } }) => (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
            plan.isActive
              ? "bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400"
              : "bg-yellow-50 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400"
          }`}
        >
          {plan.isActive ? <CheckCircle size={14} /> : <EyeOff size={14} />}
          {plan.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      id: "subscriberCount",
      header: "Subscribers",
      meta: { width: "w-28" },
      cell: ({ row: { original: plan } }) => (
        <div className="text-muted-foreground">{plan.subscriberCount}</div>
      ),
    },
    {
      id: "assignments",
      header: "Assigned Brands",
      meta: { width: "w-36" },
      cell: ({ row: { original: plan } }) => {
        const count = plan.assignments.length;
        return (
          <button
            onClick={() => handleManageBrands(plan)}
            className="text-sm font-medium text-[#d7785b] hover:text-[#c5694f] hover:underline cursor-pointer transition-colors"
            title="Manage brand access"
          >
            <div className="flex items-center gap-1.5">
              <Building2 size={16} />
              <span>{count}</span>
            </div>
          </button>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      meta: { width: "w-32" },
      cell: ({ row: { original: plan } }) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleEdit(plan)}
            className="p-1.5 text-muted-foreground hover:text-[#d7785b] hover:bg-orange-50 dark:hover:bg-orange-500/10 rounded-lg transition-colors"
            title="Edit plan"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleManageBrands(plan)}
            className="p-1.5 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
            title="Manage brand access"
          >
            <Building2 size={16} />
          </button>
          <button
            onClick={() => handleToggleActive(plan)}
            className="p-1.5 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
            title={plan.isActive ? "Deactivate plan" : "Activate plan"}
          >
            {plan.isActive ? <EyeOff size={16} /> : <CheckCircle size={16} />}
          </button>
        </div>
      ),
    },
  ];

  const formFields = (
    <>
      <div>
        <Label htmlFor="plan-name">Name</Label>
        <Input
          id="plan-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Ziplox"
        />
        {isEditModalOpen && (
          <p className="text-xs text-muted-foreground mt-1">
            Renaming is blocked while a plan has active subscribers — Shopify
            subscriptions are matched by plan name.
          </p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="plan-price">Price ($/month)</Label>
          <Input
            id="plan-price"
            type="number"
            min="0"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            placeholder="499"
          />
        </div>
        <div>
          <Label htmlFor="plan-commission">Revenue share (%)</Label>
          <Input
            id="plan-commission"
            type="number"
            min="0"
            max="99"
            step="0.01"
            value={form.commissionPct}
            onChange={(e) =>
              setForm({ ...form, commissionPct: e.target.value })
            }
            placeholder="1"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="plan-ai-searches">Social searches / month</Label>
          <Input
            id="plan-ai-searches"
            type="number"
            min="0"
            value={form.aiSearchQuestions}
            onChange={(e) =>
              setForm({ ...form, aiSearchQuestions: e.target.value })
            }
            placeholder="Default for plan"
          />
        </div>
        <div>
          <Label htmlFor="plan-visibility">Visibility</Label>
          <Select
            value={form.isPublic ? "public" : "private"}
            onValueChange={(value) =>
              setForm({ ...form, isPublic: value === "public" })
            }
          >
            <SelectTrigger id="plan-visibility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Public (pricing page)</SelectItem>
              <SelectItem value="private">Private (assigned brands)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );

  return (
    <div className="space-y-8 max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-2">
        <div className="space-y-1">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
            Plans Management
          </h1>
          <p className="text-muted-foreground text-lg">
            Configure subscription plans and grant brands access to private
            plans
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              fetchPlans();
              fetchEnterpriseRequests();
            }}
            variant="outline"
            className="border-border"
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleCreate}
            className="bg-[#d7785b] hover:bg-[#c5694f] text-white"
          >
            <Plus size={16} className="mr-2" />
            Create Plan
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-transparent border border-border rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Total Plans
              </p>
              <p className="text-3xl font-bold text-foreground mt-2">
                {plans.length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-2xl border border-border bg-transparent flex items-center justify-center">
              <CreditCard className="text-muted-foreground" size={20} />
            </div>
          </div>
        </div>

        <div className="bg-transparent border border-border rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Public Plans
              </p>
              <p className="text-3xl font-bold text-foreground mt-2">
                {plans.filter((p) => p.isPublic).length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-2xl border border-border bg-transparent flex items-center justify-center">
              <Globe className="text-green-600 dark:text-green-400" size={20} />
            </div>
          </div>
        </div>

        <div className="bg-transparent border border-border rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Private Plans
              </p>
              <p className="text-3xl font-bold text-foreground mt-2">
                {plans.filter((p) => !p.isPublic).length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-2xl border border-border bg-transparent flex items-center justify-center">
              <Lock className="text-[#d7785b]" size={20} />
            </div>
          </div>
        </div>

        <div className="bg-transparent border border-border rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Open Enterprise Requests
              </p>
              <p className="text-3xl font-bold text-foreground mt-2">
                {openRequestCount}
              </p>
            </div>
            <div className="h-12 w-12 rounded-2xl border border-border bg-transparent flex items-center justify-center">
              <Sparkles className="text-[#d7785b]" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <DataTable
          data={plans}
          columns={columns}
          isLoading={loading}
          emptyText="No plans found"
        />
      </div>

      {/* Enterprise requests submitted from the pricing page */}
      <div className="space-y-3">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold text-foreground">
            Enterprise requests
          </h2>
          <p className="text-muted-foreground">
            Brands that asked for custom pricing from the Enterprise tier. Create
            a private plan for them and they'll see it on their pricing page.
          </p>
        </div>
        <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          <DataTable
            data={enterpriseRequests}
            columns={requestColumns}
            isLoading={loadingRequests}
            emptyText="No brands have requested a custom plan yet"
            onRowClick={(request) => setSelectedRequest(request)}
          />
        </div>
      </div>

      {/* Create Modal */}
      <Dialog
        open={isCreateModalOpen}
        onOpenChange={(open) => {
          setIsCreateModalOpen(open);
          if (!open) setRequestForNewPlan(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            {requestForNewPlan
              ? `Create plan for ${requestForNewPlan.companyName || requestForNewPlan.shop}`
              : "Create New Plan"}
          </DialogHeader>
          <div className="space-y-4">
            {requestForNewPlan && (
              <div className="bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 rounded-lg p-3">
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  This plan will be assigned to{" "}
                  {requestForNewPlan.companyName || requestForNewPlan.shop} as
                  soon as it's created, and their request will be marked
                  contacted.
                </p>
              </div>
            )}
            {formFields}
            <div>
              <Label htmlFor="plan-copy-from">
                Copy features & limits from
              </Label>
              <Select
                value={form.copyFromPlanId}
                onValueChange={(value) =>
                  setForm({ ...form, copyFromPlanId: value })
                }
              >
                <SelectTrigger id="plan-copy-from">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_COPY_VALUE}>
                    Don't copy (empty plan)
                  </SelectItem>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Custom brand plans usually copy features and limits from Scale.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                setRequestForNewPlan(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={isSubmitting}
              className="bg-[#d7785b] hover:bg-[#c5694f] text-white"
            >
              {isSubmitting ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>Edit Plan</DialogHeader>
          <div className="space-y-4">
            {formFields}
            <div>
              <Label htmlFor="plan-status">Status</Label>
              <Select
                value={form.isActive ? "active" : "inactive"}
                onValueChange={(value) =>
                  setForm({ ...form, isActive: value === "active" })
                }
              >
                <SelectTrigger id="plan-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditSubmit}
              disabled={isSubmitting}
              className="bg-[#d7785b] hover:bg-[#c5694f] text-white"
            >
              {isSubmitting ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brand Access Modal */}
      <Dialog open={isAssignModalOpen} onOpenChange={setIsAssignModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Building2 size={20} />
              Brand access for "{selectedPlan?.name}"
            </div>
          </DialogHeader>
          <div className="space-y-5">
            {selectedPlan?.isPublic && (
              <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  This plan is public, so every brand can already see it.
                  Assignments only matter for private plans.
                </p>
              </div>
            )}

            {/* Search & assign */}
            <div>
              <Label htmlFor="brand-search">Assign a brand</Label>
              <div className="relative mt-1">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="brand-search"
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                  placeholder="Search brands by company name…"
                  className="pl-9"
                />
              </div>
              {brandSearch.trim().length >= 2 && (
                <div className="mt-2 border border-border rounded-lg divide-y divide-border max-h-56 overflow-y-auto">
                  {searchingBrands ? (
                    <div className="flex items-center justify-center py-6">
                      <RefreshCw
                        className="animate-spin text-muted-foreground"
                        size={20}
                      />
                    </div>
                  ) : brandResults.length === 0 ? (
                    <div className="text-center py-6 text-sm text-muted-foreground">
                      No brands found
                    </div>
                  ) : (
                    brandResults.map((brand) => {
                      const alreadyAssigned = assignedSessionIds.has(
                        brand.sessionId,
                      );
                      return (
                        <div
                          key={brand.id}
                          className="flex items-center justify-between gap-3 p-3"
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate">
                              {brand.companyName || brand.username || "—"}
                            </p>
                            <p className="text-sm text-muted-foreground truncate">
                              {brand.email}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant={alreadyAssigned ? "outline" : "default"}
                            disabled={
                              alreadyAssigned ||
                              assigningSessionId === brand.sessionId
                            }
                            onClick={() => handleAssignBrand(brand)}
                            className={
                              alreadyAssigned
                                ? ""
                                : "bg-[#d7785b] hover:bg-[#c5694f] text-white"
                            }
                          >
                            {alreadyAssigned
                              ? "Assigned"
                              : assigningSessionId === brand.sessionId
                                ? "Assigning..."
                                : "Assign"}
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Current assignments */}
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Brands with access ({assignments.length})
              </p>
              {loadingAssignments ? (
                <div className="flex items-center justify-center py-6">
                  <RefreshCw className="animate-spin text-muted-foreground" size={20} />
                </div>
              ) : assignments.length === 0 ? (
                <div className="text-center py-6 text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                  No brands assigned to this plan yet
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {assignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      className="flex items-center justify-between gap-3 p-3 border border-border rounded-lg hover:bg-accent transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {assignment.companyName || assignment.shop}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {assignment.shop} · added{" "}
                          {format(new Date(assignment.createdAt), "MMM d, yyyy")}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveAssignment(assignment)}
                        disabled={removingAssignmentId === assignment.id}
                        className="p-1.5 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Revoke access"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAssignModalOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enterprise request details */}
      <Dialog
        open={Boolean(selectedRequest)}
        onOpenChange={(open) => !open && setSelectedRequest(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Sparkles size={20} />
              {selectedRequest?.companyName || selectedRequest?.shop}
            </div>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <dl className="grid grid-cols-3 gap-x-4 gap-y-3 text-sm">
                <dt className="text-muted-foreground">Store</dt>
                <dd className="col-span-2 text-foreground">
                  {selectedRequest.shop}
                </dd>
                <dt className="text-muted-foreground">Contact</dt>
                <dd className="col-span-2 text-foreground">
                  {selectedRequest.contactName}
                </dd>
                <dt className="text-muted-foreground">Email</dt>
                <dd className="col-span-2">
                  <a
                    href={`mailto:${selectedRequest.email}`}
                    className="text-[#d7785b] hover:underline"
                  >
                    {selectedRequest.email}
                  </a>
                </dd>
                <dt className="text-muted-foreground">Phone</dt>
                <dd className="col-span-2 text-foreground">
                  {selectedRequest.phone || "—"}
                </dd>
                <dt className="text-muted-foreground">Monthly revenue</dt>
                <dd className="col-span-2 text-foreground">
                  {selectedRequest.monthlyRevenue || "—"}
                </dd>
                <dt className="text-muted-foreground">Current plan</dt>
                <dd className="col-span-2 text-foreground">
                  {selectedRequest.currentPlan || "None"}
                </dd>
                <dt className="text-muted-foreground">Requested</dt>
                <dd className="col-span-2 text-foreground">
                  {format(
                    new Date(selectedRequest.createdAt),
                    "MMM d, yyyy 'at' h:mm a",
                  )}
                </dd>
              </dl>

              <div>
                <p className="text-sm text-muted-foreground mb-1">What they need</p>
                <p className="text-sm text-foreground whitespace-pre-wrap border border-border rounded-lg p-3 bg-muted">
                  {selectedRequest.message || "No message provided"}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedRequest(null)}>
              Close
            </Button>
            <Button
              onClick={() =>
                selectedRequest && handleCreatePlanForRequest(selectedRequest)
              }
              className="bg-[#d7785b] hover:bg-[#c5694f] text-white"
            >
              <Plus size={16} className="mr-2" />
              Create plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
